import React, { useState, useEffect } from 'react';
import { Database, Eye, EyeOff, Download, Upload, RefreshCw, Trash2, Copy, Check, Users } from 'lucide-react';
import myobFirebaseService from 'services/myobFirebaseService';
import { useAuthContext } from 'context/auth-context';
import toast from 'react-hot-toast';

const MYOBFirebaseTokenManager = () => {
    const { currentUserData } = useAuthContext();
    const [tokens, setTokens] = useState(null);
    const [showTokens, setShowTokens] = useState(false);
    const [isRefreshing, setIsRefreshing] = useState(false);
    const [allUsers, setAllUsers] = useState([]);
    const [isLoading, setIsLoading] = useState(false);

    useEffect(() => {
        if (currentUserData?.id) {
            myobFirebaseService.setCurrentUser(currentUserData.id);
            loadTokens();
        }
    }, [currentUserData]);

    const loadTokens = async () => {
        try {
            const storedTokens = await myobFirebaseService.loadTokensFromFirebase();
            setTokens(storedTokens);
        } catch (error) {
            console.error('Error loading tokens:', error);
            toast.error('Failed to load tokens from database');
        }
    };

    const loadAllUsers = async () => {
        setIsLoading(true);
        try {
            const users = await myobFirebaseService.getAllUsersWithTokens();
            setAllUsers(users);
        } catch (error) {
            console.error('Error loading users:', error);
            toast.error('Failed to load users with tokens');
        } finally {
            setIsLoading(false);
        }
    };

    const toggleTokenVisibility = () => {
        setShowTokens(!showTokens);
    };

    const copyToClipboard = async (text, label) => {
        try {
            await navigator.clipboard.writeText(text);
            toast.success(`${label} copied to clipboard!`);
        } catch (error) {
            toast.error('Failed to copy to clipboard');
        }
    };

    const exportTokens = () => {
        if (!tokens) {
            toast.error('No tokens to export');
            return;
        }

        const tokenData = {
            ...tokens,
            exported_at: new Date().toISOString(),
            note: 'MYOB OAuth Tokens from Firebase Database - Keep secure!'
        };

        const blob = new Blob([JSON.stringify(tokenData, null, 2)], {
            type: 'application/json'
        });
        
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `myob-tokens-firebase-${new Date().toISOString().split('T')[0]}.json`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
        
        toast.success('Tokens exported successfully!');
    };

    const refreshTokens = async () => {
        console.log('🔄 Refresh button clicked');
        console.log('Current tokens:', tokens);
        
        if (!tokens?.refresh_token) {
            console.log('❌ No refresh token available');
            toast.error('No refresh token available');
            return;
        }

        console.log('✅ Refresh token found, starting refresh process...');
        setIsRefreshing(true);
        
        try {
            console.log('🔄 Calling myobFirebaseService.refreshAccessToken()...');
            const refreshedTokens = await myobFirebaseService.refreshAccessToken();
            console.log('✅ Token refresh successful:', refreshedTokens);
            
            console.log('🔄 Reloading tokens from Firebase...');
            await loadTokens();
            
            console.log('✅ Token refresh process completed successfully');
            toast.success('Tokens refreshed successfully!');
        } catch (error) {
            console.error('❌ Token refresh failed:', error);
            console.error('Error details:', {
                message: error.message,
                stack: error.stack,
                name: error.name
            });
            toast.error(`Failed to refresh tokens: ${error.message}`);
        } finally {
            setIsRefreshing(false);
            console.log('🔄 Refresh process finished, isRefreshing set to false');
        }
    };

    const clearTokens = async () => {
        if (window.confirm('Are you sure you want to clear all MYOB tokens from the database?')) {
            try {
                await myobFirebaseService.clearTokensFromFirebase();
                setTokens(null);
                toast.success('Tokens cleared successfully!');
            } catch (error) {
                toast.error(`Failed to clear tokens: ${error.message}`);
            }
        }
    };

    const formatToken = (token) => {
        if (!token) return 'Not available';
        if (showTokens) {
            return token;
        }
        return `${token.substring(0, 20)}...${token.substring(token.length - 10)}`;
    };

    const formatDate = (timestamp) => {
        if (!timestamp) return 'Not available';
        
        // Handle Firestore Timestamp objects
        if (timestamp && typeof timestamp === 'object' && timestamp.toDate) {
            return timestamp.toDate().toLocaleString();
        }
        
        // Handle regular Date objects or timestamps
        return new Date(timestamp).toLocaleString();
    };

    const isTokenExpired = () => {
        if (!tokens?.expires_at) return false;
        
        // Handle Firestore Timestamp objects
        let expiresAt;
        if (tokens.expires_at && typeof tokens.expires_at === 'object' && tokens.expires_at.toDate) {
            expiresAt = tokens.expires_at.toDate().getTime();
        } else {
            expiresAt = tokens.expires_at;
        }
        
        return Date.now() >= expiresAt;
    };

    if (!tokens) {
        return (
            <div className="bg-white rounded-lg shadow-md p-6" style={{ display: 'none' }}>
                <div className="flex items-center gap-2 mb-4">
                    <Database className="w-5 h-5 text-blue-600" />
                    <h3 className="text-lg font-semibold text-gray-800">MYOB Firebase Token Manager</h3>
                </div>
                <div className="text-center py-8">
                    <div className="text-gray-500 mb-4">
                        <Database className="w-12 h-12 mx-auto mb-2" />
                        <p>No MYOB tokens found in Firebase database</p>
                        <p className="text-sm">Complete the OAuth flow to get tokens</p>
                    </div>
                    <button
                        onClick={loadTokens}
                        className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
                    >
                        Refresh
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div className="bg-white rounded-lg shadow-md p-6" style={{ display: 'none' }}>
            <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2">
                    <Database className="w-5 h-5 text-blue-600" />
                    <h3 className="text-lg font-semibold text-gray-800">MYOB Firebase Token Manager</h3>
                </div>
                <div className="flex gap-2">
                    <button
                        onClick={toggleTokenVisibility}
                        className="flex items-center gap-2 px-3 py-1 text-sm bg-gray-100 hover:bg-gray-200 rounded-md transition-colors"
                    >
                        {showTokens ? <EyeOff size={16} /> : <Eye size={16} />}
                        {showTokens ? 'Hide' : 'Show'} Tokens
                    </button>
                </div>
            </div>

            <div className="space-y-4">
                {/* Token Status */}
                <div className="flex items-center gap-2">
                    <div className={`w-3 h-3 rounded-full ${isTokenExpired() ? 'bg-red-500' : 'bg-green-500'}`}></div>
                    <span className="text-sm font-medium">
                        {isTokenExpired() ? 'Token Expired' : 'Token Active'}
                    </span>
                    {tokens.expires_at && (
                        <span className="text-sm text-gray-500">
                            (Expires: {formatDate(tokens.expires_at)})
                        </span>
                    )}
                </div>

                {/* Storage Location */}
                <div className="p-3 bg-blue-50 border border-blue-200 rounded-md">
                    <div className="flex items-center gap-2">
                        <Database className="w-4 h-4 text-blue-600" />
                        <span className="text-sm font-medium text-blue-800">Stored in Firebase Database</span>
                    </div>
                    <p className="text-sm text-blue-700 mt-1">
                        Tokens are securely stored in Firestore and accessible from any device
                    </p>
                </div>

                {/* Access Token */}
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                        Access Token
                    </label>
                    <div className="flex items-center gap-2">
                        <input
                            type="text"
                            value={formatToken(tokens.access_token)}
                            readOnly
                            className="flex-1 px-3 py-2 border border-gray-300 rounded-md bg-gray-50 text-sm font-mono"
                        />
                        <button
                            onClick={() => copyToClipboard(tokens.access_token, 'Access token')}
                            className="p-2 text-gray-500 hover:text-gray-700 transition-colors"
                        >
                            <Copy size={16} />
                        </button>
                    </div>
                </div>

                {/* Refresh Token */}
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                        Refresh Token
                    </label>
                    <div className="flex items-center gap-2">
                        <input
                            type="text"
                            value={formatToken(tokens.refresh_token)}
                            readOnly
                            className="flex-1 px-3 py-2 border border-gray-300 rounded-md bg-gray-50 text-sm font-mono"
                        />
                        <button
                            onClick={() => copyToClipboard(tokens.refresh_token, 'Refresh token')}
                            className="p-2 text-gray-500 hover:text-gray-700 transition-colors"
                        >
                            <Copy size={16} />
                        </button>
                    </div>
                </div>

                {/* Business Info */}
                {tokens.business_id && (
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            Business ID
                        </label>
                        <input
                            type="text"
                            value={tokens.business_id}
                            readOnly
                            className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-50 text-sm font-mono"
                        />
                    </div>
                )}

                {tokens.business_name && (
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            Business Name
                        </label>
                        <input
                            type="text"
                            value={tokens.business_name}
                            readOnly
                            className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-50 text-sm"
                        />
                    </div>
                )}

                {/* Metadata */}
                <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                        <span className="font-medium">Created:</span>
                        <br />
                        <span className="text-gray-600">{formatDate(tokens.createdAt)}</span>
                    </div>
                    <div>
                        <span className="font-medium">Updated:</span>
                        <br />
                        <span className="text-gray-600">{formatDate(tokens.updatedAt)}</span>
                    </div>
                </div>

                {/* Action Buttons */}
                <div className="flex flex-wrap gap-2 pt-4 border-t">
                    <button
                        onClick={refreshTokens}
                        disabled={isRefreshing || !tokens.refresh_token}
                        className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    >
                        <RefreshCw size={16} className={isRefreshing ? 'animate-spin' : ''} />
                        {isRefreshing ? 'Refreshing...' : 'Refresh Tokens'}
                    </button>

                    <button
                        onClick={exportTokens}
                        className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors"
                    >
                        <Download size={16} />
                        Export Tokens
                    </button>

                    <button
                        onClick={clearTokens}
                        className="flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 transition-colors"
                    >
                        <Trash2 size={16} />
                        Clear Tokens
                    </button>
                </div>

                {/* Admin Section */}
                {currentUserData?.role === 'admin' && (
                    <div className="pt-4 border-t">
                        <div className="flex items-center justify-between mb-2">
                            <h4 className="text-sm font-medium text-gray-700">Admin: All Users with Tokens</h4>
                            <button
                                onClick={loadAllUsers}
                                disabled={isLoading}
                                className="flex items-center gap-2 px-3 py-1 text-sm bg-purple-600 text-white rounded-md hover:bg-purple-700 disabled:opacity-50 transition-colors"
                            >
                                <Users size={16} />
                                {isLoading ? 'Loading...' : 'Load All Users'}
                            </button>
                        </div>
                        
                        {allUsers.length > 0 && (
                            <div className="space-y-2">
                                {allUsers.map((user, index) => (
                                    <div key={index} className="p-2 bg-gray-50 rounded text-sm">
                                        <div className="font-medium">User ID: {user.userId}</div>
                                        <div className="text-gray-600">
                                            Business: {user.business_name || 'Unknown'}
                                        </div>
                                        <div className="text-gray-600">
                                            Updated: {formatDate(user.updatedAt)}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                )}

                {/* Security Warning */}
                <div className="bg-yellow-50 border border-yellow-200 rounded-md p-3">
                    <p className="text-sm text-yellow-800">
                        <strong>Security Note:</strong> Tokens are stored securely in Firebase Firestore. 
                        They are encrypted in transit and at rest, and only accessible to authenticated users.
                    </p>
                </div>
            </div>
        </div>
    );
};

export default MYOBFirebaseTokenManager;
